import { useState, useEffect, useCallback, useMemo } from 'react';
import { ButtonGroup, Button } from 'react-bootstrap';
import { useUIConfig } from './Context';
import { CSS_LABEL, getCSS } from './propEditorComponents/css';
import PropsInfo from './PropsInfo';
import RenderParams from './propEditorComponents/RenderParams';
import { useTranslation } from 'react-i18next';
import GenericDropdown from '../../../controls/GenericDropdown';
import BUILD_TIME_CONFIG_TYPES from '../../../../config/buildTimeConfigTypes';
import ExpressionBuilderModal from './modals/ExpressionBuilderModal';

const PropEditor = ({ maximizeButton }) => {
    const [translate] = useTranslation();
    const [data, setData] = useState([]);
    const [uniqueDataTabs, setUniqueDataTabs] = useState([]);
    const [selectedTab, setSelectedTab] = useState('');
    const [allowCreateArgs, setAllowCreateArgs] = useState(false);

    const { bcs, outerBc, setOuterBc, propOverrides, propConfigOverrides, jsxComponents, propEditorPath, edit, reset, remove, saveChanges, reservedArgs,
        components, blocks, applets, selectedLayoutItemType, selectedLayoutItem, getInitialValue, showAdvancedProps, setShowAdvancedProps, DEFAULT_TAB } = useUIConfig();

    const computeKeyCss = useCallback((entry, overridesObj) => {
        if (entry.keys) {
            entry.keys.map(key => {
                computeKeyCss(key, overridesObj[key.name] || {});
                if (key.name === CSS_LABEL) {
                    const data = getCSS({
                        param: key,
                        css: overridesObj[key.name] || key.defaultValue || {},
                        cssPlus: overridesObj['cssPlus'] || {},
                        cssMinus: overridesObj['cssMinus'] || {}
                    });
                    data.initialValue = {};
                    data.initialCssPlus = overridesObj['cssPlus'] || {};
                    data.initialCssMinus = overridesObj['cssMinus'] || {};

                    data.keys.forEach(nestedKey => data.initialValue[nestedKey.name] = nestedKey.initialValue);
                    return data;
                }
                return key;
            });
        }
    }, []);

    useEffect(() => {
        setAllowCreateArgs(false);
        if (!propOverrides || !propOverrides.path) {
            setData({ ERROR: `LayoutItem not found` });
        }
        else {
            const metadata = jsxComponents.find(({ name }) => name === propOverrides.path);
            if (!metadata) {
                setData({ ERROR: `Configure does not support components of type '${propOverrides.path}'` });
            }
            else {
                const propData = [];
                metadata.params.filter(({ visible }) => visible !== false).forEach((entry) => {
                    computeKeyCss(entry, propOverrides[entry.name] || {});
                    if (entry.name === CSS_LABEL) {
                        const data = getCSS({
                            param: entry,
                            css: propOverrides[entry.name] || entry.defaultValue || {},
                            cssPlus: propOverrides['cssPlus'] || {},
                            cssMinus: propOverrides['cssMinus'] || {}
                        });
                        data.initialValue = {};
                        data.initialCssPlus = propOverrides['cssPlus'] || {};
                        data.initialCssMinus = propOverrides['cssMinus'] || {};
                        data.overridesValue = {};
                        if (propConfigOverrides) {
                            data.overridesValue = propConfigOverrides['cssPlus'] || propConfigOverrides['cssMinus'];
                        }

                        data.keys.forEach(key => {
                            data.initialValue[key.name] = key.initialValue;
                        });
                        propData.push(data);
                    }
                    else if (entry.type === 'CustomKeyObject') {
                        const keys = propOverrides[entry.name] ?
                            Object.keys(propOverrides[entry.name]).map(key => ({
                                name: key,
                                allowRemove: true,
                                type: 'String'
                            }))
                            : [];
                        propData.push({
                            ...entry,
                            keys,
                            initialValue: propOverrides[entry.name] !== undefined ? propOverrides[entry.name] : entry.defaultValue,
                            overridesValue: propConfigOverrides && propConfigOverrides[entry.name]
                        });
                    }
                    else {
                        propData.push({
                            ...entry,
                            initialValue: getInitialValue(propOverrides[entry.name], entry.defaultValue, entry.type),
                            overridesValue: propConfigOverrides && propConfigOverrides[entry.name]
                        });
                    }
                });

                if (metadata.allowCreateArgs === true) {
                    Object.keys(propOverrides).forEach(key => {
                        if (!propData.find(entry => entry.name === key) && !reservedArgs.includes(key)) {
                            propData.push({
                                name: key,
                                type: 'String',
                                allowRemove: true,
                                initialValue: propOverrides[key]
                            })
                        }
                    })
                }
                setAllowCreateArgs(metadata.allowCreateArgs === true);

                const uniqueTabs = propData.map(param => param.tab || DEFAULT_TAB).sort((a, b) => a.split('.')[0] - b.split('.')[0]).filter((value, index, array) => array.indexOf(value) === index);
                setUniqueDataTabs(uniqueTabs);
                if (!uniqueTabs.includes(selectedTab)) {
                    setSelectedTab(uniqueTabs[0]);
                }
                setData(propData);
            }
        }
    }, [jsxComponents, propOverrides, propConfigOverrides, reservedArgs, getInitialValue, selectedTab, DEFAULT_TAB, computeKeyCss]);

    const updateProps = useCallback((_, key, value) => {
        const metaData = data.find(({ name }) => name === key);
        if (metaData && value !== metaData.initialValue) {
            edit(propEditorPath, key, value);
        }
    }, [propEditorPath, edit, data]);

    const onAddKey = useCallback((key, value) => {
        edit(propEditorPath, key, value);
        setData(prev => ([...prev, {
            name: key, allowRemove: true, type: 'String', initialValue: value
        }]));
    }, [edit, propEditorPath]);

    const dataHasAdvancedProps = useCallback((data) => {
        let hasAdvancedProps = false;
        data.forEach(param => {
            if (param.advancedProp) {
                hasAdvancedProps = true;
            }

            if (param.keys && dataHasAdvancedProps(param.keys)) {
                hasAdvancedProps = true;
            }
        });

        return hasAdvancedProps;
    }, []);

    const selectedLayoutItemExists = useMemo(() => {
        if (!components[selectedLayoutItem] && !blocks[selectedLayoutItem] && !applets[selectedLayoutItem]) {
            return false;
        }

        return true;
    }, [components, blocks, applets, selectedLayoutItem]);

    const renderTabs = useMemo(() => (
        <div className="mt-2">
            <ButtonGroup className="w-100">
                {uniqueDataTabs.map((tab) => (
                    !data.ERROR && (!data.filter(param => (param.tab === tab || (tab === DEFAULT_TAB && param.tab === undefined))).every(param => param.advancedProp) || showAdvancedProps) && <Button
                        variant={selectedTab === tab ? 'primary' : 'secondary'}
                        className="btn-sm"
                        onClick={() => setSelectedTab(tab)}
                        key={tab}>
                        {tab.split('.')[1] ? tab.split('.')[1] : tab}
                    </Button>
                ))}
            </ButtonGroup>
        </div>
    ), [selectedTab, uniqueDataTabs, showAdvancedProps, data, DEFAULT_TAB]);

    const propertyForm = useMemo(() => {
        if (!propOverrides || !propOverrides.path) {
            return null;
        }

        if (!selectedLayoutItemExists) {
            return null;
        }

        if (data.ERROR) {
            return <p>{data.ERROR}</p>;
        }

        return (
            <div className="pb-4">
                <PropsInfo
                    maximizeButton={maximizeButton}
                    jsx={propOverrides.path}
                    withinBlockName={propOverrides.BLOCK}
                    withinLayoutItemName={propOverrides.LAYOUTITEM}
                    allowCreateArgs={allowCreateArgs}
                    addKeyCallback={onAddKey}
                />
                {dataHasAdvancedProps(data) && <Button
                    onClick={() => setShowAdvancedProps(!showAdvancedProps)}
                    className="mt-2 btn-sm">
                    {showAdvancedProps ? translate("OTM_HIDE_ADVANCED_PROPS") : translate("OTM_SHOW_ADVANCED_PROPS")}
                </Button>}
                {selectedLayoutItemType !== BUILD_TIME_CONFIG_TYPES.COMPONENTS && <GenericDropdown
                    defaultValue={outerBc}
                    options={bcs ? Object.keys(bcs).map((bc) => ({ name: bc })) : []}
                    valueKey='name'
                    size='sm'
                    displayValueKey='name'
                    readOnly={true}
                    heading={translate('OTM_MOCK_CONTEXT_FOR_LAYOUT_ITEM')}
                    onSelect={(id) => setOuterBc(id)}
                />}
                {renderTabs}
                <RenderParams
                    params={uniqueDataTabs.length > 1 ? data.filter(param => (param.tab === selectedTab) || (selectedTab === DEFAULT_TAB && param.tab === undefined)) : data}
                    disabled={propOverrides.BLOCK}
                    currentPath={propEditorPath}
                    updateCallback={updateProps}
                    resetCallback={(paramName) => { reset(propEditorPath, paramName); saveChanges(); }}
                    removeCallback={remove} />
            </div>
        );
    }, [maximizeButton, propOverrides, data, allowCreateArgs, selectedLayoutItemExists, propEditorPath, renderTabs, bcs, selectedTab, uniqueDataTabs, reset, remove,
        saveChanges, DEFAULT_TAB, showAdvancedProps, setShowAdvancedProps, outerBc, setOuterBc, selectedLayoutItemType, translate, dataHasAdvancedProps, onAddKey, updateProps]);

    return (
        <>
            <ExpressionBuilderModal />
            {propertyForm}
        </>
    );
};

export default PropEditor;